// This store's open holds, and the actions on them. Online only: holds are
// shared across tills, so the server's list is the only one that counts.
// Holds put on a bill are closed as sold once that bill is saved; that check
// runs when the bill changes and on each refresh.
import { useCallback, useEffect, useRef, useState } from "react";
import db from "../../../../database";
import { fashionPosService } from "../../../../services";
import {
  buildCloseHoldMutation,
  buildCreateHoldMutation,
  buildHoldsQuery,
  forgetHolds,
  loadPending,
  readHolds,
  readMessage,
  rememberHoldOnBill,
  settlePending,
} from "../../../../utils/fashion/holds";
import { FASHION_FEATURES } from "../../../../utils/fashion/features";

export const HOLDS_REFRESH_MS = 5 * 60 * 1000;

// `enabled` false (holds are switched off until their table exists) leaves the
// list empty and makes no calls.
export default function useFashionHolds(tillData, isOnline, currentOrderId, enabled = FASHION_FEATURES.holds) {
  const bunitId = enabled ? tillData?.tillAccess?.csBunit?.csBunitId : undefined;
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const [state, setState] = useState({ holds: [], status: "idle" });
  const orderRef = useRef(currentOrderId);
  orderRef.current = currentOrderId;

  const close = useCallback(
    async (holdId, outcome, orderId) => readMessage(await fashionPosService(buildCloseHoldMutation(holdId, outcome, orderId)), "closeFashionHold", "Couldn't close the hold. Try again."),
    []
  );

  const settle = useCallback(async () => {
    const pending = loadPending(bunitId);
    if (pending.length === 0) return;
    const done = await settlePending(pending, {
      currentOrderId: orderRef.current,
      getOrder: (id) => db.orders.get(id),
      close: (holdId, orderId) => close(holdId, "SLD", orderId),
    });
    if (done.length) forgetHolds(bunitId, done);
  }, [bunitId, close]);

  const reload = useCallback(async () => {
    if (!bunitId || !online) return;
    setState((s) => ({ ...s, status: s.holds.length ? "done" : "loading" }));
    await settle();
    const holds = readHolds(await fashionPosService(buildHoldsQuery(bunitId)));
    setState((s) => (holds === null ? { holds: s.holds, status: "error" } : { holds, status: "done" }));
  }, [bunitId, online, settle]);

  useEffect(() => {
    if (!bunitId || !online) return undefined;
    reload();
    const timer = setInterval(reload, HOLDS_REFRESH_MS);
    return () => clearInterval(timer);
  }, [bunitId, online, reload]);

  // A new bill means the last one was saved, parked or dropped.
  useEffect(() => {
    if (bunitId && online) settle();
  }, [currentOrderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useCallback(
    async (hold) => {
      const res = readMessage(await fashionPosService(buildCreateHoldMutation({ ...hold, bunitId })), "createFashionHold", "Couldn't save the hold. Check the connection and try again.");
      if (res.ok) reload();
      return res;
    },
    [bunitId, reload]
  );

  const release = useCallback(
    async (holdId) => {
      const res = await close(holdId, "REL");
      if (res.ok) {
        forgetHolds(bunitId, [holdId]);
        reload();
      }
      return res;
    },
    [bunitId, close, reload]
  );

  const putOnBill = useCallback((hold) => rememberHoldOnBill(bunitId, { holdId: hold.id, sOrderID: orderRef.current, productId: hold.productId, heldUntil: hold.heldUntil }), [bunitId]);

  return { ...state, enabled, online, reload, create, release, putOnBill };
}
